import React, { useCallback, useMemo } from "react";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import { KC_Text_Sizes, KC_Weights } from "../../../designsystem/ds_commons";
import KC_Text from "../../../designsystem/typography/KC_Text/KC_Text";
import KC_Heading3 from "../../../designsystem/typography/KC-Heading/KC_Heading3";
import KC_Button, {
  KC_ButtonSize
} from "../../../designsystem/KC_Button/KC_Button";
import NiceModal from "@ebay/nice-modal-react";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import { tt } from "../../../utils/i18n/i18n";
import { TranslationsKeys } from "../../../utils/i18n/translations/Translations";
import ConfirmModal from "../../../components/modal/ConfirmModal/ConfirmModal";
import TwemojiWrapper from "../../../components/TwemojiWrapper/TwemojiWrapper";
import { useSubscriptionPlansSelector } from "../../../redux/actions/app";
import FreeTrialModal from "./FreeTrialModal";

export default function UpsellBanner() {
  const { accountToDisplay } = useAccountToDisplaySelector();
  const allSubscriptionPlans = useSubscriptionPlansSelector();

  const handleFreeTrialClick = useCallback(async () => {
    if (!accountToDisplay?.token) {
      return;
    }

    const confirmed = await NiceModal.show(FreeTrialModal, {
      initialPhoneNumber:
        accountToDisplay?.businessUser?.contactPhoneNumber || "",
      token: accountToDisplay.token,
      trackingID: accountToDisplay.trackingID,
      description: "Asked for free trial"
    });

    if (confirmed) {
      void NiceModal.show(ConfirmModal, {
        title: tt("free_trial_success_title"),
        children: (
          <TwemojiWrapper isText>
            {tt("free_trial_success_message")} ☎
          </TwemojiWrapper>
        ),
        hideAction: true
      });
    }
  }, [
    accountToDisplay?.token,
    accountToDisplay?.trackingID,
    accountToDisplay?.businessUser?.contactPhoneNumber
  ]);

  const FREE_PLANS_IDS = useMemo(() => {
    const ids =
      allSubscriptionPlans?.filter((p) => !p.price).map((p) => p._id) ?? [];
    return ["", null, undefined, ...ids];
  }, [allSubscriptionPlans]);

  const PREMIUM_PLANS_IDS = useMemo(() => {
    return (
      allSubscriptionPlans
        ?.filter((p) => p.saasFeatures?.ip || (p.price && p.price > 0))
        ?.map((p) => p._id) ?? []
    );
  }, [allSubscriptionPlans]);

  const CONTENT = useMemo(() => {
    if (PREMIUM_PLANS_IDS.includes(accountToDisplay?.saasPlanId ?? "")) {
      return (
        <>
          <div className="w-100 d-flex flex-column gap-sm finalize-container">
            <KC_UIText
              text={tt("upsell_banner_paid_title")}
              twemoji={true}
              weight={KC_Weights.bold}
              className="text-primary mb-3"
            />
            <div className="d-flex flex-column">
              {PAID_TIPS.map((key) => (
                <KC_Text
                  key={key}
                  size={KC_Text_Sizes.md}
                  weight={KC_Weights.reg}
                  text={tt(key)}
                  className="text-primary"
                />
              ))}
            </div>
          </div>
        </>
      );
    }

    if (
      FREE_PLANS_IDS.includes(accountToDisplay?.saasPlanId ?? "") &&
      !!accountToDisplay?.businessUser?.clientsPerDay &&
      accountToDisplay?.businessUser?.clientsPerDay !== "less_than_50"
    ) {
      return (
        <div className="d-flex flex-column upsell w-100">
          <div>
            <div className="w-100 d-flex">
              <KC_Heading3
                text={tt("upsell_banner_free_title")}
                className="text-primary mb-3"
              />
            </div>
            <div className="d-flex flex-column">
              {FREE_BENEFITS.map((key) => (
                <KC_Text
                  key={key}
                  size={KC_Text_Sizes.md}
                  weight={KC_Weights.reg}
                  text={tt(key)}
                  className="text-primary"
                />
              ))}
            </div>
          </div>

          {!accountToDisplay?.businessUser?.pid ? (
            <KC_Button
              size={KC_ButtonSize.Giant}
              isUpsell={true}
              isPrimary={false}
              type="button"
              onClick={handleFreeTrialClick}
              text={tt("upsell_banner_free_trial_button")}
              className="d-flex w-auto ml-auto mt-auto"
            />
          ) : (
            <div className="d-flex flex-column justify-content-end">
              <KC_UIText
                weight="bold"
                text={tt("free_trial_request_sent")}
                className="text-right"
              />
            </div>
          )}
        </div>
      );
    }

    return null;
  }, [
    accountToDisplay?.saasPlanId,
    accountToDisplay?.businessUser?.pid,
    handleFreeTrialClick,
    PREMIUM_PLANS_IDS,
    FREE_PLANS_IDS
  ]);

  return (
    <div className="d-flex align-items-center justify-content-between">
      {CONTENT}
    </div>
  );
}

const PAID_TIPS: (keyof TranslationsKeys)[] = [
  "upsell_banner_paid_tip_1",
  "upsell_banner_paid_tip_2",
  "upsell_banner_paid_tip_3",
  "upsell_banner_paid_tip_4",
  "upsell_banner_paid_tip_5"
];

const FREE_BENEFITS: (keyof TranslationsKeys)[] = [
  "upsell_banner_free_benefit_1",
  "upsell_banner_free_benefit_2",
  "upsell_banner_free_benefit_3",
  "upsell_banner_free_benefit_4",
  "upsell_banner_free_benefit_5"
];
